import { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { initializeAPI } from './utils/apiHelper';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './css/ifia.css';
import './css/Spacers.css';
import './css/responsive.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Toaster } from 'react-hot-toast';
import { AudioProvider } from './contexts/AudioContext';

// Components
import Header from './components/Common/Header';
import WelcomeHeader from './components/Common/WelcomeHeader';
import ScrollToTop from './components/Common/ScrollToTop';
import Footer from './components/Common/Footer';
import AudioModal from './components/Common/AudioModal';

// Pages
import Home from './pages/Home';
import ActivityLog from './pages/ActivityLog';

// Initialize FontAwesome library
library.add(fas);

// Create a new AppContent component to use Auth0 hooks
function AppContent() {
  const { logout, isAuthenticated, user, getAccessTokenSilently, isLoading, loginWithRedirect } = useAuth0();
  const [isInitialized, setIsInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const initializationAttempted = useRef(false);
  const TIMEOUT_DURATION = 24 * 60 * 60 * 1000; // 24 hours (for indexing - bring back down to 20 minutes later)

  useEffect(() => {
       
    if (!isLoading && !isAuthenticated) {
  
      loginWithRedirect();
      return;
    }

    const initializeApiHelper = async () => {
      if (isAuthenticated && user && getAccessTokenSilently && !initializationAttempted.current) {
        initializationAttempted.current = true;
        try {
          const success = await initializeAPI(getAccessTokenSilently);
          if (success) {
            setIsInitialized(true);
            setAuthError(null); // Clear any previous errors
          } else {
            console.error('API initialization failed - authentication required');
            setAuthError('Authentication initialization failed. Please try again.');
            // Reset the attempt flag to allow retry
            initializationAttempted.current = false;
            // DO NOT set initialized to true - block access
          }
        } catch (error) {
          console.error('API initialization error:', error);
          setAuthError(`Authentication failed: ${error.message}`);
          // Reset the attempt flag if initialization fails
          initializationAttempted.current = false;
          // DO NOT allow app to continue - authentication is required
          console.error('Authentication failed - access denied');
        }
      }
    };

    initializeApiHelper();
  }, [isLoading, isAuthenticated, user, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Simple activity update without state changes
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const events = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart', 'toast'];
    
    // Add activity listeners
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Only check for timeout in the interval
    const intervalId = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity >= TIMEOUT_DURATION) {

        handleLogout();
      }
    }, 60000); // Check every minute

    // Clean up event listeners and interval
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout({ 
      logoutParams: {
        returnTo: window.location.origin 
      }
    });
  };

  const handleRetryAuth = () => {
    setAuthError(null);
    initializationAttempted.current = false;
    // Trigger re-initialization
    if (isAuthenticated && user && getAccessTokenSilently) {
      const initializeApiHelper = async () => {
        initializationAttempted.current = true;
        try {
          const success = await initializeAPI(getAccessTokenSilently);
          if (success) {
            setIsInitialized(true);
            setAuthError(null);
          } else {
            console.error('API initialization failed - authentication required');
            setAuthError('Authentication initialization failed. Please try again.');
            initializationAttempted.current = false;
          }
        } catch (error) {
          console.error('API initialization error:', error);
          setAuthError(`Authentication failed: ${error.message}`);
          initializationAttempted.current = false;
          console.error('Authentication failed - access denied');
        }
      };
      initializeApiHelper();
    }
  };

  const hideNav = new URLSearchParams(window.location.search).get('fullScreen') === 'true';
  const hideFooter = new URLSearchParams(window.location.search).get('fullScreen') === 'true';

  if (isLoading) {
    return (
      <div className="initial-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading Auth0...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="initial-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Redirecting to login...</span>
      </div>
    );
  }

  if (!isInitialized) {
    if (authError) {
      return (
        <div className="initial-loading">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <FontAwesomeIcon icon={faSpinner} style={{ color: '#dc3545', marginBottom: '10px' }} />
            <h3 style={{ color: '#dc3545', marginBottom: '10px' }}>Authentication Error</h3>
            <p style={{ marginBottom: '20px' }}>{authError}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button 
                onClick={handleRetryAuth}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry Authentication
              </button>
              <button 
                onClick={handleLogout}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Logout & Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="initial-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Initializing authentication...</span>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop/>
      <div className="content-wrapper">
        {!hideNav && (
          <>
            <Header onLogout={handleLogout} />
            <WelcomeHeader />
          </>
        )}
        <Routes>
          <Route path="/" element={<Home isInitialized={isInitialized} currentUser={user} />} />
          <Route path="/home" element={<Home isInitialized={isInitialized} currentUser={user} />} />
          <Route path="/activity-log" element={<ActivityLog />} />
        </Routes>
        {!hideFooter && <Footer />}
      </div>
      <AudioModal />
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        style={{ 
          zIndex: 999999, 
          position: 'fixed'
        }}
        toastStyle={{ 
          zIndex: 999999
        }}
      />
    </>
  );
}

// Main App component
function App() {
  const onRedirectCallback = (appState) => {
    window.history.replaceState(
      {},
      document.title,
      appState?.returnTo || window.location.pathname
    );
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: "openid profile email offline_access",
        acr_values: "http://schemas.openid.net/pape/policies/2007/06/multi-factor"
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <Router>
        <AudioProvider>
          <Toaster 
            position="bottom-right"
            toastOptions={{
              duration: 2000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          <AppContent />
        </AudioProvider>
      </Router>
    </Auth0Provider>
  );
}

export default App;
